import { useContext, useEffect, useState } from 'react';
// import { useOktaAuth } from '@okta/okta-react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    List,
    ListItem,
    Typography,
} from '@deere/fuel-react';
import DownloadIcon from '@deere/ux.brand-foundations/icons/download.svg';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { AlertBox } from '../../components/AlertBox';
import { AdminContext } from '../../context/AdminContext';
import { LoggedInContext } from '../../context/LoggedInContext';
import { errorBannerFromResponse } from '../../utils/errorBanner';
import styles from './ActiveJobBids.module.css';

const ActiveJobRow = ({ activeJob }) => {
    const navigate = useNavigate();
    return (
        <ListItem
            onClick={() => navigate(`/active-jobs/${activeJob.jobNumber}`)}
            className={styles.jobBidRow}
        >
            <Grid container className={styles.jobBidRowInner}>
                <Grid container item xs={12} sm={9.5} md={9}>
                    <Grid item xs={12} className={styles.jobBidRowTitle}>
                        <span>
                            {activeJob.classification} #{activeJob.jobNumber}{' '}
                        </span>
                        <span>- ({activeJob.noOfOpenings}) openings</span>
                    </Grid>
                    <Grid item xs={12} className={styles.jobBidInfoSection}>
                        <span>{activeJob.facility}</span>
                        <span>Dept: {activeJob.department}</span>
                        <span>Job Grade: {activeJob.jobGrade}</span>
                        <span>Bid Close {activeJob.endDate}</span>
                        <span>
                            Bid Withdrawal Close {activeJob.withdrawalEndDate}
                        </span>
                    </Grid>
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={2.5}
                    md={3}
                    className={styles.jobBiddersSection}
                >
                    <span>Number of Bidders</span>
                    <span>{activeJob.bidCount}</span>
                </Grid>
            </Grid>
        </ListItem>
    );
};

export const ActiveJobBids = () => {
    const [activeJobBids, setActiveJobBids] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [alertBanner, setAlertBanner] = useState();
    const [isAutoBidsInProgress, setIsAutoBidsInProgress] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const { authState, makeRequest } = useContext(LoggedInContext);
    const { selectedAdminGroup } = useContext(AdminContext);

    const comparator = (a, b) => {
        const facilityA = a.facility.toUpperCase(); // ignore upper and lowercase
        const facilityB = b.facility.toUpperCase(); // ignore upper and lowercase
        if (facilityA < facilityB) {
            return -1;
        }
        if (facilityA > facilityB) {
            return 1;
        }
        if (facilityA === facilityB) {
            const departmentA = a.department.toString().toUpperCase(); // ignore upper and lowercase
            const departmentB = b.department.toString().toUpperCase(); // ignore upper and lowercase
            if (departmentA < departmentB) {
                return -1;
            }
            if (departmentA > departmentB) {
                return 1;
            }
        }
        return 0;
    };

    useEffect(() => {
        let ignore = false;
        const fetchBids = async () => {
            setIsLoading(true);
            if (!ignore) {
                const response = await makeRequest(
                    `${process.env.REACT_APP_API_ENDPOINT}/jobs/active`
                );
                if (response.ok) {
                    const responseData = await response.json();
                    const jobBids = responseData
                        .map((job) => ({
                            jobNumber: job.id,
                            facility: job.facility,
                            department: job.department,
                            classification: job.classification.classification,
                            noOfOpenings: job.noOfOpenings,
                            jobGrade: job.jobGrade,
                            endDate: DateTime.fromISO(job.endDate).toFormat(
                                'LL/dd/yy'
                            ),
                            withdrawalEndDate: DateTime.fromISO(
                                job.withdrawalEndDate
                            ).toFormat('LL/dd/yy'),
                            reqNumber: job.reqNumber,
                            bidCount: job.numberOfBidders ?? 0,
                        }))
                        .sort(comparator);
                    setActiveJobBids(jobBids);
                } else {
                    setAlertBanner(
                        errorBannerFromResponse(
                            response,
                            'We are unable to fetch active jobs at this time.'
                        )
                    );
                }
            }
            setIsLoading(false);
        };
        fetchBids();
        // cleanup function that will ignore future results of fetch triggered by the useEffect hook
        return () => {
            ignore = true;
        };
    }, [authState, makeRequest]);

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setUploadedFile(file);
        }
    };

    const handleAutoBids = async () => {
        setIsAutoBidsInProgress(true);
        setAlertBanner({
            variant: 'info',
            message: 'Starting auto bids...',
        });

        try {
            const formData = new FormData();
            formData.append('file', uploadedFile);

            // Fire and forget - don't wait for completion
            makeRequest(`${process.env.REACT_APP_API_ENDPOINT}/startAutoBids`, {
                method: 'POST',
                body: formData,
                headers: {
                    'content-type': null, // Remove content-type, let browser set multipart/form-data with boundary
                },
            })
                .then((response) => {
                    if (response.ok) {
                        console.log('Auto-bid process started successfully');
                        return response.json();
                    } else {
                        console.error('Failed to start auto-bid process');
                    }
                })
                .catch((error) => {
                    console.error('Auto-bid request error:', error);
                });

            // Immediate success message - don't wait for backend processing
            setAlertBanner({
                variant: 'success',
                message:
                    'Auto bids have been started successfully and are processing in the background. You will receive an email notification when complete.',
            });
            setUploadedFile(null);
            setIsAutoBidsInProgress(false);
        } catch (error) {
            setAlertBanner(errorBannerFromResponse(error));
            setIsAutoBidsInProgress(false);
        }
    };

    const activeJobDownload = async () => {
        const response = await makeRequest(
            `${process.env.REACT_APP_API_ENDPOINT}/activeJobsDownload`
        );
        if (response.ok) {
            const filename = response.headers
                .get('content-disposition')
                .split('filename=')[1];
            const activeJobDownloadResponse = await response.blob();
            const fileURL = window.URL.createObjectURL(
                activeJobDownloadResponse
            );
            const link = document.createElement('a');
            link.href = fileURL;
            link.download = filename;
            link.target = '_blank';
            link.click();
            window.URL.revokeObjectURL(fileURL);
            link.remove();
        } else {
            setAlertBanner(
                errorBannerFromResponse(
                    response,
                    'We are unable to download the jobs spreadsheet.'
                )
            );
        }
    };

    return (
        <div className={styles.parentContainer}>
            <Box className={styles.contentToContainer}>
                <Box
                    className={`${styles.headerContainer} ${styles.pageInstructions}`}
                >
                    <Typography variant="h1" className={styles.heading}>
                        Active Job Bids
                    </Typography>
                    {selectedAdminGroup?.adGroup === 'JobBidAdmin-R00' && (
                        <Box className={styles.autoBidsButtonGroup}>
                            <input
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={handleFileUpload}
                                style={{ display: 'none' }}
                                id="auto-bids-file-input"
                            />
                            <label htmlFor="auto-bids-file-input">
                                <Button
                                    component="span"
                                    variant="secondary"
                                    disabled={isLoading || isAutoBidsInProgress}
                                >
                                    {uploadedFile
                                        ? uploadedFile.name
                                        : 'Upload File'}
                                </Button>
                            </label>
                            <Button
                                className={styles.buttonDownload}
                                variant="primary"
                                disabled={
                                    !uploadedFile ||
                                    activeJobBids.length === 0 ||
                                    isLoading ||
                                    isAutoBidsInProgress
                                }
                                onClick={handleAutoBids}
                            >
                                Start Auto Bids
                            </Button>
                        </Box>
                    )}
                </Box>
                <Box>
                    {alertBanner && (
                        <AlertBox
                            msg={alertBanner.message}
                            variant={alertBanner.variant}
                        />
                    )}
                </Box>
                <Box className={styles.pageInstructions}>
                    Click on the job title to view job bid information and the
                    list of bidders for that job.
                    <Button
                        className={styles.buttonDownload}
                        variant="primary"
                        disabled={activeJobBids.length === 0 || isLoading}
                        startIcon={<img alt=">>" src={DownloadIcon} />}
                        onClick={activeJobDownload}
                    >
                        Download Active Job List
                    </Button>
                </Box>
                {isLoading && (
                    <CircularProgress
                        size="lg"
                        variant="indeterminate"
                        title={'Loading'}
                    />
                )}
                {!isLoading && activeJobBids.length === 0 && (
                    <Card>
                        <CardContent>
                            <Typography gutterBottom variant="h4">
                                There are no active jobs at this time.
                            </Typography>
                        </CardContent>
                    </Card>
                )}
                {!isLoading && activeJobBids.length > 0 && (
                    <>
                        <Card>
                            <List>
                                {activeJobBids.map((activeJob, index) => (
                                    <ActiveJobRow
                                        activeJob={activeJob}
                                        key={index}
                                    />
                                ))}
                            </List>
                        </Card>
                    </>
                )}
            </Box>
        </div>
    );
};
